import React, { type FormEvent, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { SupText } from "@repo/core-ui";

interface AmountInputProps {
  value: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  color?: "primary" | "success" | "info" | "error" | "warning";
  className?: string;
  inputClassName?: string;
  suffix?: ReactNode;
  hintFormatter?: (value: string) => string;
  onChange: (value: string) => void;
  onFocus?: (e: FormEvent<HTMLInputElement>) => void;
  onBlur?: (e: FormEvent<HTMLInputElement>) => void;
}

export const borderColors = {
  primary: "border-0.5 border-neutral-500 enabled:hover:border-neutral-700 enabled:focus:border-neutral-500 enabled:active:border-neutral-800",
  success: "border-0.5 border-success-900",
  info: "border-0.5 border-info-800",
  error: "border-0.5 border-error-925",
  warning: "border-0.5 border-warning-900",
} as const;

export function AmountInput({
  id,
  disabled,
  name,
  placeholder,
  value,
  color = "primary",
  className,
  inputClassName,
  suffix,
  hintFormatter,
  onChange,
  onBlur,
  onFocus,
}: AmountInputProps) {
  const handleStateChange = (e: FormEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value);
  };

  return (
    <div className={twMerge("sup-amount relative w-full", className)}>
      <input
        disabled={disabled}
        id={id}
        name={name}
        type="text"
        className={twMerge(
          "sup-amount-input w-full font-mono font-normal text-2xl leading-5 tracking-normal outline-0 pl-2 pt-2 pb-8 transition-colors duration-200 placeholder:text-mono placeholder:text-2xl placeholder:leading-5 placeholder:text-neutral-600 border-0.5 bg-neutral-50 enabled:hover:bg-neutral-75 enabled:focus:bg-neutral-50 enabled:active:bg-neutral-75 disabled:bg-neutral-50 disabled:text-neutral-300",
          suffix ? "pr-16" : "pr-2",
          hintFormatter ? "pb-8" : "pb-2",
          borderColors[color],
          inputClassName
        )}
        placeholder={placeholder}
        value={value}
        onChange={handleStateChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />

      {Boolean(hintFormatter) && (
        <SupText as="span" variant="subtitle2" className="absolute pointer-events-none left-2 bottom-2 leading-5 w-full">
          {hintFormatter?.(value)}
        </SupText>
      )}

      {Boolean(suffix) && (
        <SupText as="span" variant="subtitle1" className="absolute pointer-events-none top-2 right-2 leading-5">
          {suffix}
        </SupText>
      )}
    </div>
  );
}
